import { AvatarPlaceholder } from "assets/icons";
import { FilesUploader } from "components";
import { IMAGE_FORMATS, MAX_IMAGE_FILE_SIZE } from "constants/common";
import { MediaDirectoryKeys } from "constants/enums";
import { getAcceptObjectForFilesInput } from "helpers";
import { DirectUploadMedia } from "libs/directUpload";
import { useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import { useForm } from "react-hook-form";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { FormField, Input, Modal } from "ui";

import {
  CompanyOperationalContact,
  useUpsertCompanyContact
} from "../../../apis";

import styles from "./OperationalContactModal.module.scss";

const FORM_ID = "operational-contact-form";

interface FormValues {
  fullName: string;
  role: string;
  email: string;
  phoneNumber: string;
  avatarMediaId: string | null;
}

interface OperationalContactModalProps {
  isOpen: boolean;
  companyId: string;
  contact?: CompanyOperationalContact | null;
  onClose: () => void;
}

interface AvatarPickerProps {
  value: DirectUploadMedia | null;
  onDrop: (files: File[]) => void;
}

const AvatarPicker = ({ value, onDrop }: AvatarPickerProps) => {
  const { t } = useTranslation();
  const { getRootProps, getInputProps, open } = useDropzone({
    accept: getAcceptObjectForFilesInput({
      acceptImageFormats: IMAGE_FORMATS,
      acceptVideoFormats: [],
      acceptDocumentFormats: [],
      isImagesAllowed: true,
      isVideosAllowed: false,
      isDocumentsAllowed: false
    }),
    maxFiles: 1,
    maxSize: MAX_IMAGE_FILE_SIZE,
    noDrag: true,
    onDropAccepted: onDrop,
    onDropRejected: (rejections) => {
      const message = rejections[0]?.errors[0]?.message;
      toast.error(message || t("File rejected"));
    }
  });

  return (
    <>
      <div {...getRootProps({ className: styles.form__avatarPicker })}>
        <input {...getInputProps()} />
        {value?.url ? (
          <img src={value.url} alt={t("Avatar")} />
        ) : (
          <AvatarPlaceholder width={100} height={100} />
        )}
      </div>
      <span className={styles.form__avatarHint} onClick={open}>
        {t("Change photo")}
      </span>
    </>
  );
};

const OperationalContactModal = ({
  isOpen,
  companyId,
  contact,
  onClose
}: OperationalContactModalProps) => {
  const { t } = useTranslation();

  const initialAvatar: DirectUploadMedia | null = contact?.avatar
    ? (contact.avatar as unknown as DirectUploadMedia)
    : null;

  const [avatarMedia, setAvatarMedia] = useState<DirectUploadMedia | null>(
    initialAvatar
  );

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors }
  } = useForm<FormValues>({
    defaultValues: {
      fullName: "",
      role: "",
      email: "",
      phoneNumber: "",
      avatarMediaId: null
    }
  });

  useEffect(() => {
    if (isOpen) {
      reset({
        fullName: contact?.fullName ?? "",
        role: contact?.role ?? "",
        email: contact?.email ?? "",
        phoneNumber: contact?.phoneNumber ?? "",
        avatarMediaId: contact?.avatar?.id ?? null
      });
      setAvatarMedia(initialAvatar);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, contact?.id]);

  const { mutate, isLoading } = useUpsertCompanyContact(companyId, {
    onSuccess: () => {
      toast.success(t("Saved"));
      onClose();
    },
    onError: (e) => toast.error(e?.message || t("Failed to save"))
  });

  const onAvatarUploaded = (media: DirectUploadMedia) => {
    setAvatarMedia(media);
    setValue("avatarMediaId", media.id);
  };

  const onSubmit = (values: FormValues) => {
    mutate({
      fullName: values.fullName,
      role: values.role || null,
      email: values.email || null,
      phoneNumber: values.phoneNumber || null,
      avatarMediaId: values.avatarMediaId
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t("Operational contact")}
      formId={FORM_ID}
      okText={t("Save contact")}
      cancelText={t("Cancel")}
      isLoading={isLoading}
      className={styles.modal}
    >
      <form
        id={FORM_ID}
        className={styles.form}
        onSubmit={handleSubmit(onSubmit)}
      >
        <div className={styles.form__avatarBlock}>
          <FilesUploader
            onSuccessUploading={onAvatarUploaded}
            uploadDirectoryKey={MediaDirectoryKeys.DeliveryCompany}
          >
            {({ onFilesDrop }) => (
              <AvatarPicker value={avatarMedia} onDrop={onFilesDrop} />
            )}
          </FilesUploader>
        </div>

        <div className={styles.form__divider} />

        <div className={styles.form__fields}>
          <FormField
            label={t("Full name")}
            errorMessage={errors.fullName?.message}
          >
            <Input
              {...register("fullName", { required: t("Required field") })}
              placeholder={t("e.g. Fatou Ndiaye")}
            />
          </FormField>

          <FormField
            label={t("Role / Title")}
            errorMessage={errors.role?.message}
          >
            <Input
              {...register("role")}
              placeholder={t("e.g. Account Director")}
            />
          </FormField>

          <FormField label={t("Email")} errorMessage={errors.email?.message}>
            <Input
              type="email"
              {...register("email")}
              placeholder="name@company.ci"
            />
          </FormField>

          <FormField
            label={t("Phone Number")}
            errorMessage={errors.phoneNumber?.message}
          >
            <Input
              {...register("phoneNumber")}
              placeholder={t("Enter Phone Number")}
            />
          </FormField>
        </div>
      </form>
    </Modal>
  );
};

export default OperationalContactModal;
